if (!customElements.get('and-rider-fit')) {
  customElements.define('and-rider-fit', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const initialize = () => {
        if (!this.isConnected) return;
        const data = this.querySelector('[data-model-data]');
        if (!data) return;
        this.copy = JSON.parse(this.querySelector('[data-fit-copy]')?.textContent || '{}');
        this.imageBounds = this.imageBounds || new WeakMap();
        this.imagePending = this.imagePending || new WeakSet();
        this.models = JSON.parse(data.textContent).map(model => ({ ...model, min: Number(model.min), max: Number(model.max), length: Number(model.length), bikeHeight: Number(model.bikeHeight) }));
        this.unit = this.dataset.units === 'auto' ? (this.dataset.country === 'US' ? 'imperial' : 'cm') : this.dataset.units;
        if (!['cm', 'imperial'].includes(this.unit)) this.unit = 'cm';
        this.height = Math.min(215, Math.max(140, Number(this.dataset.height) || 175));
        this.gender = this.gender || 'male';
        this.modelId = (this.models.find(model => model.name === this.dataset.defaultModel) || this.models[0])?.id;
        const options = { signal: this.controller.signal };
        this.addEventListener('input', event => {
          if (event.target.matches('[data-height-slider]')) { this.height = Number(event.target.value); this.render(); }
        }, options);
        this.addEventListener('change', event => {
          if (event.target.matches('[data-model-select]')) { this.compareId = this.modelId; this.modelId = event.target.value; this.render(); }
          if (event.target.matches('[data-comparison-select]')) { this.compareId = event.target.value; this.render(); }
          if (event.target.matches('[data-cm]')) {
            const value = Number(event.target.value);
            if (event.target.value === '' || !Number.isFinite(value) || value < 140 || value > 215) {
              event.target.setCustomValidity('Enter a height between 140 and 215 cm.'); event.target.reportValidity(); return;
            }
            event.target.setCustomValidity(''); this.height = value; this.render();
          }
          if (event.target.matches('[data-feet], [data-inches]')) {
            const feet = this.querySelector('[data-feet]'), inches = this.querySelector('[data-inches]');
            feet.setCustomValidity(''); inches.setCustomValidity('');
            const f = Number(feet.value), i = Number(inches.value), value = (f * 12 + i) * 2.54;
            if (feet.value === '' || inches.value === '' || !Number.isInteger(f) || !Number.isInteger(i) || i < 0 || i > 11 || value < 140 || value > 215) {
              event.target.setCustomValidity('Enter feet and inches within the slider range. Inches must be 0 to 11.'); event.target.reportValidity(); return;
            }
            this.height = value; this.render();
          }
        }, options);
        this.addEventListener('input', event => {
          if (event.target.matches('[data-cm], [data-feet], [data-inches]')) event.target.setCustomValidity('');
        }, options);
        this.addEventListener('click', event => {
          const gender = event.target.closest('[data-gender]');
          if (gender) { this.gender = gender.dataset.gender; this.render(); }
          const step = event.target.closest('[data-height-step]');
          if (step) {
            const increment = this.unit === 'imperial' ? 2.54 : 1;
            this.height = Math.min(215, Math.max(140, this.height + Number(step.dataset.heightStep) * increment));
            this.render();
          }
          const unit = event.target.closest('[data-unit]'), row = event.target.closest('[data-model-row]');
          if (unit) { this.unit = unit.dataset.unit; this.render(); }
          if (row) { this.compareId = this.modelId; this.modelId = row.dataset.modelRow; this.render(); }
        }, options);
        document.addEventListener('shopify:block:select', event => {
          if (this.models.some(model => model.id === event.detail.blockId)) { this.modelId = event.detail.blockId; this.render(); }
        }, options);
        this.querySelectorAll('.arf__bike img').forEach(image => {
          image.addEventListener('load', () => this.prepareBikeImage(image), options);
          if (image.complete && image.naturalWidth) this.prepareBikeImage(image);
        });
        this.render();
        this.visualObserver?.disconnect();
        this.visualObserver = new ResizeObserver(() => this.fitVisual());
        this.visualObserver.observe(this.querySelector('.arf__stage'));
      };
      if (this.querySelector('[data-model-data]')) initialize(); else requestAnimationFrame(initialize);
    }
    disconnectedCallback() { this.controller?.abort(); this.visualObserver?.disconnect(); }
    format(cm, unit = this.unit) {
      if (unit === 'cm') return `${Math.round(cm * 10) / 10} cm`;
      const inches = Math.round(cm / 2.54);
      return `${Math.floor(inches / 12)}′${inches % 12}″`;
    }
    ready(model) { return model.ready === true && Number.isFinite(model.min) && Number.isFinite(model.max) && model.min > 0 && model.max > model.min; }
    copyText(key, fallback, model) {
      let text = this.copy[key] || fallback;
      if (key === 'fit_title' && model.titleOverride) text = model.titleOverride;
      if (key === 'fit_message' && model.messageOverride) text = model.messageOverride;
      const values = { model: model.name, min: this.format(model.min), max: this.format(model.max), height: this.format(this.height) };
      return text.replace(/\{(model|min|max|height)\}/g, (match, name) => values[name]);
    }
    result(model) {
      if (!this.ready(model)) return { type: 'unknown', short: 'Range pending', title: this.copyText('unknown_title', 'Height range to be confirmed', model), detail: this.copyText('unknown_message', 'Please contact our team to confirm the rider height range for {model}.', model) };
      const epsilon = 0.00001;
      if (this.height >= model.min - epsilon && this.height <= model.max + epsilon) {
        return { type: 'within', short: 'Within range', title: this.copyText('fit_title', 'Recommended for Your Height', model), detail: this.copyText('fit_message', 'Your height is within the recommended range for {model}. Please also check your inseam and the bike dimensions for the best fit.', model) };
      }
      const delta = this.height < model.min ? model.min - this.height : this.height - model.max;
      if (delta <= 3) return { type: 'edge', short: 'Check fit', title: this.copyText('edge_title', 'Near the range — check your measurements', model), detail: this.copyText('edge_message', 'Your height is just outside the published range. Please confirm inseam, standover, saddle height and reach before choosing.', model) };
      return { type: 'outside', short: 'Outside range', title: this.copyText('outside_title', 'Outside the recommended height range', model), detail: this.copyText('outside_message', 'Please check another model or contact our team to confirm a suitable fit.', model) };
    }
    prepareBikeImage(image) {
      if (this.imagePending.has(image) || this.imageBounds.has(image)) return;
      this.imagePending.add(image);
      const source = new Image(); source.crossOrigin = 'anonymous';
      source.onload = () => {
        try {
          const ratio = Math.min(1, 256 / Math.max(source.naturalWidth, source.naturalHeight));
          const width = Math.max(1, Math.round(source.naturalWidth * ratio)), height = Math.max(1, Math.round(source.naturalHeight * ratio));
          const canvas = document.createElement('canvas'); canvas.width = width; canvas.height = height;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(source, 0, 0, width, height);
          const pixels = context.getImageData(0, 0, width, height).data;
          const corner = [0, width - 1, width * (height - 1), width * height - 1];
          const bg = [0, 0, 0, 0];
          corner.forEach(index => { for (let c = 0; c < 4; c++) bg[c] += pixels[index * 4 + c] / 4; });
          let left = width, top = height, right = -1, bottom = -1;
          for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
            const index = (y * width + x) * 4, alpha = pixels[index + 3];
            const diff = Math.max(Math.abs(pixels[index] - bg[0]), Math.abs(pixels[index + 1] - bg[1]), Math.abs(pixels[index + 2] - bg[2]));
            const foreground = alpha > 24 && (bg[3] < 32 || diff > 38);
            if (foreground) { left = Math.min(left, x); right = Math.max(right, x); top = Math.min(top, y); bottom = Math.max(bottom, y); }
          }
          if (right > left && bottom > top) {
            this.imageBounds.set(image, { x: left / width, y: top / height, width: (right - left + 1) / width, height: (bottom - top + 1) / height });
            if (this.isConnected) this.fitVisual();
          }
        } catch (error) {
          image.style.objectPosition = 'center bottom';
        }
      };
      source.onerror = () => { image.style.objectPosition = 'center bottom'; };
      source.src = image.currentSrc || image.src;
    }
    alignBikeImage(image) {
      const bounds = this.imageBounds?.get(image), box = image.parentElement;
      if (!bounds || !box.clientWidth || !box.clientHeight) return;
      const scale = Math.min(box.clientWidth / (image.naturalWidth * bounds.width), box.clientHeight / (image.naturalHeight * bounds.height));
      const width = image.naturalWidth * scale, height = image.naturalHeight * scale;
      image.style.position = 'absolute'; image.style.maxWidth = 'none'; image.style.width = width + 'px'; image.style.height = height + 'px';
      image.style.left = (box.clientWidth - width * bounds.width) / 2 - width * bounds.x + 'px';
      image.style.top = box.clientHeight - height * (bounds.y + bounds.height) + 'px';
      image.style.objectFit = 'fill';
    }
    fitVisual() {
      const model = this.models?.find(item => item.id === this.modelId), stage = this.querySelector('.arf__stage');
      if (!model || !stage) return;
      const usableWidth = stage.clientWidth;
      if (usableWidth <= 0) return;
      const length = model.length > 100 && model.length < 300 ? model.length : 190;
      const scale = Math.min(usableWidth / (length + 12), (stage.clientHeight - 30) / 215);
      const rider = this.querySelector('.arf__rider'), bike = this.querySelector('.arf__bike-images');
      rider.style.left = (usableWidth - 70 * scale) / 2 + 'px'; rider.style.width = 70 * scale + 'px'; rider.style.height = this.height * scale + 'px';
      bike.style.left = (usableWidth - length * scale) / 2 + 'px'; bike.style.width = length * scale + 'px';
      bike.style.height = (model.bikeHeight > 50 && model.bikeHeight < 180 ? model.bikeHeight : 120) * scale + 'px';
      this.querySelectorAll('.arf__bike img').forEach(image => this.alignBikeImage(image));
    }
    render() {
      const selected = this.models.find(model => model.id === this.modelId);
      if (!selected) return;
      const put = (selector, text) => { const element = this.querySelector(selector); if (element) element.textContent = text; };
      const single = this.models.length === 1;
      this.querySelector('[data-model-select]').hidden = single;
      this.querySelector('.arf__model-label').hidden = single;
      this.querySelector('[data-single-model]').hidden = !single;
      this.querySelector('.arf__compare').hidden = single;
      put('[data-single-model-name]', selected.name);
      this.querySelectorAll('[data-gender]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.gender === this.gender)));
      this.querySelectorAll('[data-silhouette]').forEach(svg => svg.toggleAttribute('hidden', svg.dataset.silhouette !== this.gender));
      const imperial = this.unit === 'imperial', roundedInches = Math.round(this.height / 2.54), progress = ((this.height - 140) / 75) * 100;
      put('[data-height-output]', this.format(this.height));
      put('[data-height-secondary]', this.format(this.height, imperial ? 'cm' : 'imperial'));
      put('[data-scale-min]', this.format(140)); put('[data-scale-max]', this.format(215));
      put('[data-rider-label]', this.format(this.height)); put('[data-selected-name]', selected.name);
      put('[data-model-range]', this.ready(selected) ? `${this.format(selected.min)} – ${this.format(selected.max)}` : 'To be confirmed');
      put('[data-model-note]', selected.note || '');
      this.querySelectorAll('[data-unit]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.unit === this.unit)));
      this.querySelector('[data-cm-entry]').hidden = imperial; this.querySelector('[data-imperial-entry]').hidden = !imperial;
      this.querySelector('[data-cm]').value = Math.round(this.height * 10) / 10;
      this.querySelector('[data-feet]').value = Math.floor(roundedInches / 12); this.querySelector('[data-inches]').value = roundedInches % 12;
      const slider = this.querySelector('[data-height-slider]'); slider.value = this.height; slider.setAttribute('aria-valuetext', this.format(this.height)); slider.style.setProperty('--arf-progress', `${progress}%`);
      this.querySelector('[data-model-select]').value = selected.id;
      this.querySelectorAll('[data-height-step]').forEach(button => button.setAttribute('aria-label', `${Number(button.dataset.heightStep) > 0 ? 'Increase' : 'Decrease'} height by ${imperial ? 'one inch' : 'one centimetre'}`));
      this.fitVisual();
      this.querySelectorAll('[data-model-image]').forEach(element => element.hidden = element.dataset.modelImage !== selected.id);
      const result = this.result(selected), resultElement = this.querySelector('[data-result]'); resultElement.dataset.state = result.type;
      put('[data-result-title]', result.title); put('[data-result-detail]', result.detail);
      const candidates = this.models.filter(model => model.id !== selected.id);
      if (!candidates.some(model => model.id === this.compareId)) this.compareId = candidates[0]?.id;
      const comparisonSelect = this.querySelector('[data-comparison-select]');
      comparisonSelect.replaceChildren(...candidates.map(model => { const option = document.createElement('option'); option.value = model.id; option.textContent = model.name; return option; }));
      comparisonSelect.value = this.compareId || '';
      this.querySelectorAll('[data-model-row]').forEach(row => {
        const model = this.models.find(item => item.id === row.dataset.modelRow), valid = this.ready(model), state = this.result(model);
        row.setAttribute('aria-pressed', String(model.id === selected.id)); row.dataset.state = state.type;
        const compareVisible = model.id === selected.id || model.id === this.compareId;
        row.hidden = !compareVisible; row.dataset.compareVisible = String(compareVisible);
        row.querySelector('[data-row-range]').textContent = valid ? `${this.format(model.min)} – ${this.format(model.max)}` : 'To be confirmed';
        row.querySelector('[data-row-status]').textContent = state.short;
        const band = row.querySelector('[data-band]'); band.hidden = !valid;
        if (valid) {
          const left = Math.max(0, Math.min(100, (model.min - 140) / 75 * 100));
          const right = Math.max(0, Math.min(100, (model.max - 140) / 75 * 100));
          band.style.left = `${left}%`; band.style.width = `${Math.max(0, right - left)}%`;
        }
        row.querySelector('[data-marker]').style.left = `${progress}%`;
      });
    }
  });
}
